// services/authService.ts
import { 
  signInWithPhoneNumber,
  signInWithCredential,
  PhoneAuthProvider,
  updateProfile,
  User,
  RecaptchaVerifier
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import type { UserProfile, EducationLevel } from '../types/user';

let recaptchaVerifier: RecaptchaVerifier | null = null;

export const authService = {
  /**
   * Sets up Firebase's built-in RecaptchaVerifier.
   * Firebase handles reCAPTCHA internally — no need for a separate
   * Enterprise script or manual grecaptcha.enterprise.execute() calls.
   * The verifier is created fresh each time to avoid stale state.
   */
  setupRecaptcha(containerId: string) {
    // Always destroy & recreate to prevent stale verifier issues
    if (recaptchaVerifier) {
      try { recaptchaVerifier.clear(); } catch (_) { /* noop */ }
      recaptchaVerifier = null;
    }
    recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
      size: 'invisible',
    });
    return recaptchaVerifier;
  },

  /**
   * Resets the reCAPTCHA verifier (call after failed OTP attempt
   * or before retrying). Firebase requires a fresh verifier after
   * each signInWithPhoneNumber call.
   */
  resetRecaptcha(containerId: string) {
    if (recaptchaVerifier) {
      try { recaptchaVerifier.clear(); } catch (_) { /* noop */ }
      recaptchaVerifier = null;
    }
    return this.setupRecaptcha(containerId);
  },

  async sendOtp(phoneNumber: string): Promise<string> {
    // Ensure +91 prefix
    const formattedPhone = phoneNumber.startsWith('+91') ? phoneNumber : `+91${phoneNumber}`;
    
    if (!recaptchaVerifier) {
      throw new Error('reCAPTCHA not initialized. Please reload the page.');
    }

    try {
      const confirmationResult = await signInWithPhoneNumber(auth, formattedPhone, recaptchaVerifier);
      // Firebase consumes the verifier after each call — mark as used
      recaptchaVerifier = null;
      return confirmationResult.verificationId;
    } catch (err: any) {
      // Firebase also invalidates the verifier on failure
      console.error('sendOtp error:', err?.code, err?.message);
      recaptchaVerifier = null;
      throw err;
    }
  },

  async verifyOtp(verificationId: string, code: string): Promise<User | null> {
    const credential = PhoneAuthProvider.credential(verificationId, code);
    const result = await signInWithCredential(auth, credential);
    return result.user;
  },

  async createUserProfile(user: User, profile: { displayName: string; email: string; currentEducation: EducationLevel }): Promise<UserProfile> {
    await updateProfile(user, { displayName: profile.displayName });
    
    const userProfile: UserProfile = {
      uid: user.uid,
      phoneNumber: user.phoneNumber || '',
      displayName: profile.displayName,
      email: profile.email,
      currentEducation: profile.currentEducation,
      photoURL: user.photoURL || undefined,
      createdAt: new Date().toISOString(),
      lastLogin: new Date().toISOString(),
    };

    try {
      await setDoc(doc(db, 'users', user.uid), userProfile);
    } catch (err) {
      console.warn('Could not write user profile to Firestore (check security rules):', err);
      // Still return the profile — auth succeeded even if Firestore write failed
    }
    return userProfile;
  },

  async getUserProfile(uid: string): Promise<UserProfile | null> {
    try {
      const docRef = doc(db, 'users', uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return docSnap.data() as UserProfile;
      }
    } catch (err) {
      // Firestore security rules may deny this read — that's OK,
      // we'll fall back to building a profile from Firebase Auth data.
      console.warn('Could not read user profile from Firestore (check security rules):', err);
    }
    return null;
  },

  async getCurrentUser(): Promise<UserProfile | null> {
    const user = auth.currentUser;
    if (!user) return null;
    
    // Try to get profile from Firestore (may fail if rules deny reads)
    const profile = await this.getUserProfile(user.uid);
    if (profile) return profile;

    // Create basic profile from Firebase Auth data
    return {
      uid: user.uid,
      phoneNumber: user.phoneNumber || '',
      displayName: user.displayName || undefined,
      email: undefined,
      currentEducation: undefined,
      photoURL: user.photoURL || undefined,
      createdAt: user.metadata.creationTime || '',
      lastLogin: user.metadata.lastSignInTime || '',
    };
  },

  async signOut() {
    recaptchaVerifier = null;
    return auth.signOut();
  },

  onAuthStateChange(callback: (user: UserProfile | null) => void) {
    return auth.onAuthStateChanged(async (firebaseUser: User | null) => {
      if (firebaseUser) {
        const profile = await this.getUserProfile(firebaseUser.uid);
        if (profile) {
          callback(profile);
        } else {
          // Basic profile from Firebase Auth
          callback({
            uid: firebaseUser.uid,
            phoneNumber: firebaseUser.phoneNumber || '',
            displayName: firebaseUser.displayName || undefined,
            email: undefined,
            currentEducation: undefined,
            photoURL: firebaseUser.photoURL || undefined,
            createdAt: firebaseUser.metadata.creationTime || '',
            lastLogin: firebaseUser.metadata.lastSignInTime || '',
          });
        }
      } else {
        callback(null);
      }
    });
  },
};
